// How the screens look, for everyone — route /admin/settings.
//
// NOT A DESCRIPTOR, and that is deliberate. The generic framework's routes are keyed on
// a row's `id` and would let a caller INSERT any key it liked; the registry in
// src/services/admin/settings.js exists precisely so that it cannot, and a settings
// screen built on the framework would hand back the property the registry is for.
//
// EVERY DECLARED SETTING IS LISTED, stored or not. A screen showing only rows that exist
// would be empty on a fresh install and would hide every setting nobody had touched,
// which is all of them until it is not.
//
// RESET IS A REAL CONTROL, not a tidy-up. Absent IS the default for this table, so
// removing the row is the only way back — there is no "unset" value an admin could type,
// and no is_active to clear.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import { apiFetch } from '../../api/client.js';
import { consoleErrorMessage } from './descriptor-form.js';
import ColumnsField from '../../components/admin/ColumnsField.jsx';
import { TICKET_COLUMNS, selectionProblem } from '../../components/tickets/ticket-columns.js';
import { CUSTOMER_COLUMNS, customerSelectionProblem } from '../customers/customer-columns.js';
import { formatDateTime } from '../../components/tickets/format.js';

// The server serves the KEYS; these are the labels for them. Same split every other
// vocabulary in this console uses, and the two lists are pinned to each other by
// test/routes/admin-settings.test.js.
const LABELS = new Map([
  ...TICKET_COLUMNS.map((c) => [`tickets.default_columns:${c.key}`, c.label]),
  ...CUSTOMER_COLUMNS.map((c) => [`customers.default_columns:${c.key}`, c.label]),
]);

function SettingRow({ setting, onSave, onReset, busy }) {
  const [draft, setDraft] = useState(setting.value);
  const [dirty, setDirty] = useState(false);

  // Re-seeded when the saved value changes underneath — after a save or a reset — so the
  // control shows what is stored rather than a stale draft of what used to be.
  useEffect(() => { setDraft(setting.value); setDirty(false); }, [setting.value]);

  const setValue = (_key, next) => { setDraft(next); setDirty(true); };

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">{setting.label}</h2>
        <p className="text-xs text-ink-muted">
          {setting.is_default
            ? 'Using the default'
            : `Changed ${formatDateTime(setting.updated_at) ?? 'recently'}`}
        </p>
      </div>
      <p className="mt-1 text-xs text-ink-secondary">{setting.description}</p>

      {setting.control === 'columns' ? (
        <ColumnsField
          id={`setting-${setting.key}`}
          field={{ vocabulary: setting.vocabulary }}
          // The control labels from the TICKET catalogue by default; this screen spans
          // both, so which catalogue to name a key from depends on the setting.
          labelFor={(k) => LABELS.get(`${setting.key}:${k}`) ?? k}
          problemFor={
            setting.key === 'customers.default_columns' ? customerSelectionProblem : selectionProblem
          }
          scope="everyone who opens this screen"
          fieldKey={setting.key}
          value={draft}
          setValue={setValue}
        />
      ) : setting.control === 'toggle' ? (
        // A real checkbox, not a text box: the validator takes a BOOLEAN and would
        // refuse the string a text box posts, so an admin would meet a validation
        // error on a setting whose whole vocabulary is yes and no.
        <label className="mt-2 flex items-center gap-2 text-sm text-ink">
          <input
            id={`setting-${setting.key}`}
            type="checkbox"
            className="h-4 w-4 rounded border-line-strong"
            checked={draft === true}
            onChange={(event) => setValue(event.target.checked)}
          />
          {draft === true ? 'On' : 'Off'}
        </label>
      ) : (
        // No other control exists yet. Saying so beats rendering a text box that would
        // post a string into a validator expecting something else.
        <p className="mt-2 text-sm text-ink-muted">
          This setting has no editor in this build.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <button
          type="button"
          className="btn-primary"
          disabled={!dirty || busy}
          onClick={() => onSave(setting.key, draft)}
        >
          Save
        </button>
        <button
          type="button"
          className="btn-ghost ring-1 ring-line-strong"
          disabled={!dirty || busy}
          onClick={() => { setDraft(setting.value); setDirty(false); }}
        >
          Discard
        </button>
        <button
          type="button"
          className="btn-ghost ring-1 ring-line-strong"
          // Nothing to reset when it is already the default, and saying so with a
          // disabled control beats a button that succeeds and changes nothing.
          disabled={setting.is_default || busy}
          onClick={() => onReset(setting.key)}
        >
          Reset to default
        </button>
        {dirty && (
          <span className="text-xs text-ink-muted">
            Not saved. Everyone still sees the previous arrangement.
          </span>
        )}
      </div>
    </section>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback((signal) => apiFetch('/admin/settings', { signal })
    .then((body) => setSettings(body.data)), []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal)
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [load]);

  async function write(key, init) {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/admin/settings/${encodeURIComponent(key)}`, init);
      // Re-read the whole list rather than splicing the response in: `is_default` and
      // `updated_at` both move, and a spliced row would disagree with the server about
      // whether the Reset button should still be live.
      await load();
    } catch (err) {
      setError(consoleErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <Link to="/admin" className="text-xs text-brand-700 hover:underline dark:text-brand-300">
          ← Admin
        </Link>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-ink-secondary">
          How the screens look, for everyone. A saved view with its own columns still
          overrides the ticket list arrangement below.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {settings === null && !error && <p className="text-sm text-ink-muted">Loading…</p>}

      {settings?.map((setting) => (
        <SettingRow
          key={setting.key}
          setting={setting}
          busy={busy}
          onSave={(key, value) => write(key, { method: 'PUT', body: { value } })}
          onReset={(key) => write(key, { method: 'DELETE' })}
        />
      ))}
    </div>
  );
}
